import { useState } from "react"
import DOMPurify from "dompurify"
import Input from "@/components/core/Input/Input"
import SelectDropdown from "@/components/core/SelectDropdown/SelectDropdown"
import TextArea from "@/components/core/TextArea/TextArea"
import Button from "@/components/core/Button/Button"
import Checkbox from "@/components/core/Checkbox/Checkbox"
import styles from "@/components/CustomerSupport/Form/index.module.scss"
import { PHONE_REGEX } from "@/constants/index"
import { submitFeedback } from "@/utils/customerSupport"
import FeedbackFromConfirmation from "../FeedbackFormConfirmation"
import { validNumber } from "@/utils/validate"
import { setBannerConfirmationText } from "@/utils/helper"
import { backToCustomerSupportAnalytics, cancelFeedbackAnalytics, submitFeedbackAnalytics } from "../analytics"

const FeedbackForm = props => {
  const { staticText, authorData, resetTab } = props
  const ratingValues = ["1", "2", "3", "4", "5"]
  const [errors, setErrors] = useState({})
  const [feedbackFormData, setFeedbackFormData] = useState({})
  const [showReasons, setShowReasons] = useState(false)
  const [success, setSuccess] = useState(false)
  const validateField = name => {
    let value = feedbackFormData[name]
    if (name === "phoneNumber") {
      if (value === "")
        setErrors({ ...errors, phoneNumber: staticText.errorMobileNumber })
      else if (PHONE_REGEX.test(value))
        setErrors({ ...errors, phoneNumber: "" })
      else
        setErrors({ ...errors, phoneNumber: staticText.errorValidMobileNumber })
    }
    if (name === "requestID") {
      if (value === "" || value === undefined)
        setErrors({ ...errors, requestID: staticText.errorRequestId })
      else setErrors({ ...errors, requestID: "" })
    }
    if (name === "rating") {
      if (!value?.trim())
        setErrors({ ...errors, rating: staticText.errorRating })
      else setErrors({ ...errors, rating: "" })
    }
    if (name === "technicianOption") {
      if (!value?.trim())
        setErrors({ ...errors, technicianOption: staticText.errorSelectOption })
      else setErrors({ ...errors, technicianOption: "" })
    }
    if (
      name === "otherRatingRemarks" &&
      feedbackFormData?.reasons?.includes("Others")
    ) {
      if (value === "") {
        setErrors({ ...errors, otherRatingRemarks: staticText.errorComments })
      } else setErrors({ ...errors, otherRatingRemarks: "" })
    }
  }

  const handleResetData = (isConfirmScreen) => {
    setFeedbackFormData({})
    setSuccess(false)
    setShowReasons(false)
    scrollToTop()
    isConfirmScreen ? backToCustomerSupportAnalytics('feedback') : cancelFeedbackAnalytics()
  }

  const handleSubmit = async () => {
    const errorList = getErrorList()
    let errorDataList = []
    setErrors(errorList)
    if (errorList?.requestID) {
      errorDataList.push(`requestID`)
    } else if (errorList?.phoneNumber) {
      errorDataList.push(`phoneNumber`)
    } else if (errorList?.rating) {
      errorDataList.push(`rating`)
    } else if (errorList?.technicianOption) {
      errorDataList.push(`technicianOption`)
    } else if (errorList?.otherRatingRemarks) {
      errorDataList.push(`otherRatingRemarks`)
    }
    errorDataList.map((item, i) => {
      if (i === 0) document.getElementById(`${item}`)?.focus()
    })
    const isInvalid = Object?.values(errorList)?.find(el => el)
    if (!isInvalid) {
      const {
        requestID,
        phoneNumber,
        rating,
        reasons,
        otherCommentRemarks,
        technicianOption,
        otherRatingRemarks,
      } = feedbackFormData
      const payload = {
        customerRequestId: requestID,
        countryCode: "+91",
        mobile: phoneNumber,
        rating: rating,
        isAgentWearIdCard: technicianOption === "Yes" ? true : false,
        agentComment: otherCommentRemarks,
        feedbackReason: reasons ?? [],
        feedbackReasonComment: otherRatingRemarks,
        firstName: "",
        lastName: "",
      }
      const response = await submitFeedback(payload)
      if (response.statusCode === 200) {
        setSuccess(true)
        submitFeedbackAnalytics("success", rating, technicianOption)
        scrollToTop()
        setBannerConfirmationText(
          staticText.feedbackBannerConfirmation,
          "support-form-banner"
        )
      } else {
        setSuccess(false)
        scrollToTop()
      }
    }
  }

  const getErrorList = () => {
    const errorList = {
      requestID: validateAllFields("requestID"),
      phoneNumber: validateAllFields("phoneNumber"),
      rating: validateAllFields("rating"),
      technicianOption: validateAllFields("technicianOption"),
      reasons: validateAllFields("reasons"),
      otherRatingRemarks: validateAllFields("otherRatingRemarks"),
    }
    return errorList
  }

  const validateAllFields = name => {
    let value = feedbackFormData[name]
    if (name === "phoneNumber") {
      if (!PHONE_REGEX.test(value)) return staticText.errorMobileNumber
    }
    if (name === "requestID") {
      if (value === "" || value === undefined) return staticText.errorRequestId
    }
    if (name === "rating") {
      if (!value?.trim()) return staticText.errorRating
    }
    if (name === "technicianOption") {
      if (!value?.trim()) return staticText.errorSelectOption
    }
    if (name === "reasons" && showReasons) {
      if (value === undefined || !value.length > 0) {
        return staticText.errorSelectReasons
      }
    }
    if (
      name === "otherRatingRemarks" &&
      feedbackFormData?.reasons?.includes("Others")
    ) {
      if (value === undefined || value === "") {
        return staticText.errorComments
      }
    }
  }
  const isEnabled = Object?.values(getErrorList())?.find(el => el)
    ? true
    : false

  const onCheckboxChange = (e, option) => {
    const { checked } = e.target
    if (checked) {
      setFeedbackFormData({
        ...feedbackFormData,
        reasons: [...(feedbackFormData?.reasons || []), option.reasons],
      })
      setErrors({
        ...errors,
        reasons: "",
      })
    } else {
      let updatedReasons = feedbackFormData?.reasons?.filter(
        ele => ele !== option.reasons
      )
      let error = { ...errors }
      let feedbackForm = { ...feedbackFormData }
      feedbackForm = { ...feedbackForm, reasons: updatedReasons }
      if (!updatedReasons.length > 0)
        error = { ...error, reasons: staticText.errorSelectReasons }
      else error = { ...error, reasons: "" }
      if (option.reasons === "Others") {
        error = { ...error, otherRatingRemarks: "" }
        feedbackForm = {
          ...feedbackForm,
          otherRatingRemarks: "",
        }
      }
      setFeedbackFormData(feedbackForm)
      setErrors(error)
    }
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    })
  }

  const replaceRatingQuestion = ratingQuestion => {
    const cleanText = DOMPurify.sanitize(ratingQuestion, { ALLOWED_TAGS: [] })
    return cleanText
  }

  return (
    <div className={styles.customerSupportFeedback}>
      {!success ? (
        <div className="customer-support-feedback__feedback">
          <Input
            id="requestID"
            type="text"
            value={feedbackFormData?.requestID ?? ""}
            placeholder={staticText.requestID}
            label={staticText.requestID}
            onChange={e => {
              const { value } = e.target
              setFeedbackFormData({ ...feedbackFormData, requestID: value })
            }}
            showError={
              !errors.requestID || errors.requestID === "" ? false : true
            }
            errorMessage={errors.requestID}
            onBlur={() => validateField("requestID")}
            maxLength="40"
          />
          <div className="customer-support-feedback__phoneDiv">
            <div className="customer-support-feedback__countryIcon">
              <Input disabled value="+91" />
            </div>
            <div className="customer-support-feedback__phoneNumber">
              <Input
                id="phoneNumber"
                type="text"
                value={feedbackFormData.phoneNumber ?? ""}
                maxLength={10}
                prefix={
                  <img
                    src={"/icons/phone-icon.svg"}
                    alt=""
                    aria-hidden="true"
                  />
                }
                placeholder={staticText.registeredNumber}
                label={staticText.registeredNumber}
                name="phoneNumber"
                onChange={e => {
                  const { value } = e.target
                  if (value === "" || validNumber(value)) {
                    setFeedbackFormData({
                      ...feedbackFormData,
                      phoneNumber: value,
                    })
                  }
                }}
                showError={
                  !errors.phoneNumber || errors.phoneNumber === ""
                    ? false
                    : true
                }
                errorMessage={errors.phoneNumber}
                onBlur={() => validateField("phoneNumber")}
              />
            </div>
          </div>
          <div className="customer-support-feedback__ratingQuestionDiv customer-support-feedback__fontText">
            {replaceRatingQuestion(staticText?.ratingQuestion)}
          </div>
          <SelectDropdown
            label={staticText.selectRating}
            data={ratingValues}
            value={feedbackFormData.rating ?? ""}
            id="rating"
            name="rating"
            onChange={e => {
              setFeedbackFormData({
                ...feedbackFormData,
                rating: e,
                reasons: [],
                otherRatingRemarks: "",
              })
              setErrors({
                ...errors,
                rating: "",
                reasons: "",
                otherRatingRemarks: "",
              })
              if (e <= "3") setShowReasons(true)
              else setShowReasons(false)
            }}
            showError={!errors.rating || errors.rating === "" ? false : true}
            errorMessage={errors.rating}
            onBlur={() => validateField("rating")}
            labelAfterClick={staticText?.labelAfterClick}
          />
          {showReasons && (
            <div>
              <div className="customer-support-feedback__chooseReasonsDiv">
                {staticText?.selectReasons}
              </div>
              {errors.reasons && (
                <div className="customer-support-feedback__errorHeading">
                  {staticText.errorSelectReasons}
                </div>
              )}
              <div className="customer-support-feedback__ratingOptionsMultiple">
                {authorData?.reasonsMultifield?.map((option, index) => (
                  <div
                    className="customer-support-feedback__ratingOptions"
                    key={index}
                  >
                    <Checkbox
                      value={option.reasons}
                      textClassName={`${
                        errors.reasons
                          ? "customer-support-feedback__errorRatingOptions"
                          : ""
                      }`}
                      onChange={e => {
                        onCheckboxChange(e, option)
                      }}
                      checked={feedbackFormData?.reasons?.includes(
                        option.reasons
                      )}
                    />
                  </div>
                ))}
              </div>
              {feedbackFormData?.reasons?.includes("Others") && (
                <div>
                  <TextArea
                    label={staticText.comments}
                    placeholder={staticText.comments}
                    value={feedbackFormData.otherRatingRemarks ?? ""}
                    className="customer-support-feedback__textarea"
                    maxLength="250"
                    onChange={e => {
                      const { value } = e.target
                      setFeedbackFormData({
                        ...feedbackFormData,
                        otherRatingRemarks: value,
                      })
                    }}
                    id="otherRatingRemarks"
                    showError={
                      !errors.otherRatingRemarks ||
                      errors.otherRatingRemarks === ""
                        ? false
                        : true
                    }
                    errorMessage={errors.otherRatingRemarks}
                    onBlur={() => validateField("otherRatingRemarks")}
                  />
                </div>
              )}
            </div>
          )}
          <div
            className={`customer-support-feedback__fontText ${
              showReasons
                ? "customer-support-feedback__technicianOption"
                : "customer-support-feedback__technicianOptionDiv"
            }`}
          >
            {staticText.technicianFeedbackQuestion}
          </div>
          <SelectDropdown
            label={staticText.selectOption}
            data={["Yes", "No"]}
            id="option"
            name="technicianOption"
            value={feedbackFormData.technicianOption ?? ""}
            onChange={e => {
              setFeedbackFormData({
                ...feedbackFormData,
                technicianOption: e,
              })
              setErrors({ ...errors, technicianOption: "" })
            }}
            showError={
              !errors.technicianOption || errors.technicianOption === ""
                ? false
                : true
            }
            errorMessage={errors.technicianOption}
            onBlur={() => validateField("technicianOption")}
          />
          <div className="customer-support-feedback__remarksDiv">
            <TextArea
              label={staticText.anyOtherRemarks}
              placeholder={staticText.anyOtherRemarks}
              value={feedbackFormData.otherCommentRemarks ?? ""}
              className="customer-support-feedback__textarea"
              maxLength="250"
              onChange={e => {
                const { value } = e.target
                setFeedbackFormData({
                  ...feedbackFormData,
                  otherCommentRemarks: value,
                })
              }}
            />
          </div>
          <div className="customer-support-feedback__buttonDiv">
            <Button
              type="black"
              role="button"
              label={staticText.submit}
              className="customer-support-feedback__button"
              flexible
              onClick={() => handleSubmit()}
              disabled={isEnabled}
            />
            <Button
              className="customer-support-feedback__button"
              type="ghost"
              role="button"
              label={staticText.cancel}
              flexible
              onClick={() => handleResetData(false)}
            />
          </div>
        </div>
      ) : (
        <FeedbackFromConfirmation
          handleResetData={() => handleResetData(true)}
          staticText={staticText}
          resetTab={resetTab}
        />
      )}
    </div>
  )
}

export default FeedbackForm
